import { useMemo } from "react";
import type { SheetRow } from "@/lib/api";
import type { OntologySchema } from "@/lib/useOntology";
import {
  classOfColumn,
  columnKind,
  columnLetter,
  isThingColumn,
  mappingProblems,
  type ColumnMapping,
} from "@/lib/columnMapping";
import { Message } from "@/components/ui/Message";
import { fieldClass } from "@/components/ui/field";
import { CodeRuleEditor } from "@/components/import/mappingControls";

/**
 * Page 3 of the spreadsheet mapping (ruled 2026-09-30, 4.10): the rules. A
 * column marked Rule on page 1 stands for statements the sheet does not
 * represent. This build carries the classification rule: if the cell is x,
 * the entity in the domain column has this statement (`:type :Eligible`),
 * and a cell no row of the rule covers asserts nothing. Formula and
 * validation rules are 61.68.
 */
export function RulesPage({
  mappings,
  rows,
  schema,
  onChange,
  onFocus,
  heading = true,
}: {
  mappings: ColumnMapping[];
  rows: SheetRow[];
  schema?: OntologySchema | null;
  onChange: (next: ColumnMapping[]) => void;
  onFocus: (columns: string[]) => void;
  /** Off when the page's own heading is written above, over several sheets. */
  heading?: boolean;
}) {
  const problems = useMemo(() => mappingProblems(mappings), [mappings]);
  const rules = mappings.filter(
    (m) => columnKind(m) === "rule" && m.job === "code",
  );
  const entities = mappings.filter(isThingColumn).map((m) => m.column);
  const letter = (column: string) =>
    columnLetter(mappings.findIndex((m) => m.column === column));
  const put = (column: string, patch: Partial<ColumnMapping>) =>
    onChange(
      mappings.map((m) => (m.column === column ? { ...m, ...patch } : m)),
    );
  const first = rows[0];

  return (
    <div className="space-y-5">
      {heading && (
        <div>
          <h3 className="text-sm font-semibold">Page 3 — Rules</h3>
          <p className="text-xs text-muted-foreground">
            A Rule column stands for statements the sheet does not represent.
            Classification: if the cell is a value, the entity in the domain
            column has the statement given for it; a cell no row covers asserts
            nothing, and no cell value is stored.
          </p>
        </div>
      )}
      {rules.map((m) => {
        const subjectProblem = problems.find(
          (p) => p.column === m.column && p.field === "subject",
        );
        const domain = m.onColumn.trim();
        const value = first?.[m.column]?.trim();
        const focus = () => onFocus([m.column, domain].filter(Boolean));
        return (
          <section
            key={m.column}
            aria-label={`Rule ${m.column}`}
            className="space-y-2 rounded-md border border-border p-3"
            onMouseEnter={focus}
            onFocus={focus}
          >
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="font-mono text-xs">
                {m.column} ({letter(m.column)})
              </span>
              <span className="text-xs text-muted-foreground">
                Classification rule
              </span>
              <label className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground">Domain</span>
                <select
                  aria-label={`Domain of ${m.column}`}
                  aria-invalid={subjectProblem ? true : undefined}
                  value={domain}
                  onChange={(e) =>
                    put(m.column, {
                      onColumn: e.target.value,
                      subjectClass: "",
                    })
                  }
                  className={fieldClass(
                    subjectProblem ? "error" : "normal",
                    "min-w-28 text-xs",
                  )}
                >
                  <option value="">domain column…</option>
                  {entities.map((c) => (
                    <option key={c} value={c}>
                      {c} ({classOfColumn(mappings, c)})
                    </option>
                  ))}
                </select>
              </label>
              {value && (
                <span className="font-mono text-xs text-muted-foreground">
                  row 1: {m.column} = "{value}"
                </span>
              )}
            </div>
            {subjectProblem && (
              <Message kind="warning" density="compact">
                {subjectProblem.text}
              </Message>
            )}
            <CodeRuleEditor
              rows={m.codeMeanings}
              onChange={(codeMeanings) => put(m.column, { codeMeanings })}
              schema={schema}
            />
          </section>
        );
      })}
    </div>
  );
}
